import Image from "next/image";
import { z } from "zod";

import { SelectIcon } from "@/components/icons/select-icon";
import { RichText } from "@/components/rich-text";
import { cn } from "@/lib/utils";
import { customCardSchema } from "@/schemas";

import { VideoPlayer } from "./shared/video-player";

export function Inset({
  className,
  ...props
}: z.infer<typeof customCardSchema> & { className?: string }) {
  const {
    title,
    richTextTitle,
    icon,
    description,
    image,
    video,
    mediaType,
    bgColor,
  } = props;

  const backgroundColor = bgColor?.value ?? "#FAF9F8";

  return (
    <article
      className={cn(
        "rounded-container mx-auto flex w-full max-w-[444px] flex-col self-stretch overflow-hidden lg:max-w-none",
        className
      )}
    >
      <div
        className="flex h-[248px] w-full items-center justify-center lg:h-[392px]"
        style={{ backgroundColor }}
      >
        {image?.src && mediaType === "image" && (
          <Image
            className="size-full overflow-hidden object-cover"
            src={image.src}
            alt={image.alt ?? "Large Card Media"}
            width={image.width ?? 0}
            height={image.height ?? 0}
            sizes="(min-width: 624px) 50vw, 100vw"
          />
        )}
        {video && mediaType === "video" && video?.asset && (
          <div className="flex h-fit items-center justify-center p-4 lg:p-6">
            <VideoPlayer
              className="size-full"
              asset={video.asset}
              animation={false}
            />
          </div>
        )}
      </div>
      <div className="rounded-b-container border-grey-100 flex h-[176px] flex-col justify-between border border-t-0 p-4 lg:h-[280px] lg:p-6">
        <div className="flex items-center gap-4">
          {icon && (
            <SelectIcon id={icon} className="size-10 lg:size-14" alt={icon} />
          )}
          {richTextTitle ? (
            <div className="text-heading-24 hidden lg:block">
              <RichText value={richTextTitle} />
            </div>
          ) : (
            <span className="text-heading-24 hidden lg:block">{title}</span>
          )}
        </div>
        <div>
          {richTextTitle ? (
            <div className="text-heading-16 lg:hidden">
              <RichText value={richTextTitle} />
            </div>
          ) : (
            <span className="text-heading-16 lg:hidden">{title}</span>
          )}
          {description && (
            <p className="text-body text-grey-600 lg:text-body-md line-clamp-2">
              {description}
            </p>
          )}
        </div>
      </div>
    </article>
  );
}
